import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@mui/material'

import { useAppContext } from '@/hooks'

import { useCreateBranchDialog } from '../hooks/useCreateBranchDialog'
import { useMinimumNumberOfApprovers } from '../hooks/useMinimumNumberOfApprovers'
import { BranchRecord } from '../types'
import { CreateBranchForm } from './CreateBranchForm'

type CreateBranchDialogProps = {
	branches: BranchRecord[]
	initialSourceId?: string | null
	isOpen: boolean
	onClose: () => void
	onCreated: (branchId: string) => void
	systemId?: number
}

export const CreateBranchDialog = ({
	branches,
	initialSourceId,
	isOpen,
	onClose,
	onCreated,
	systemId,
}: CreateBranchDialogProps) => {
	const { t } = useAppContext()
	const minimumNumberOfApprovers = useMinimumNumberOfApprovers(systemId)
	const form = useCreateBranchDialog({
		branches,
		initialSourceId,
		isOpen,
		minimumNumberOfApprovers,
		onClose,
		onCreated,
		systemId,
	})

	return (
		<Dialog
			open={isOpen}
			onClose={form.handleClose}
			maxWidth="md"
			fullWidth
			slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
		>
			<DialogTitle
				data-testid="modal-header"
				sx={{ fontSize: 18, fontWeight: 700, color: '#1F4954', pb: 1 }}
			>
				{t('BRANCHES_CREATE')}
			</DialogTitle>
			<DialogContent sx={{ pt: '10px !important' }} data-testid="modal-body">
				<CreateBranchForm
					approvalsValid={form.approvalsValid}
					description={form.description}
					minimumNumberOfApprovers={form.minimumNumberOfApprovers}
					name={form.name}
					onDescriptionChange={form.setDescription}
					onNameChange={form.setName}
					onRequiredApprovalsChange={form.setRequiredApprovals}
					onSourceIdChange={form.setSourceId}
					requiredApprovals={form.requiredApprovals}
					sourceId={form.sourceId}
					sourceOptions={form.sourceOptions}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5 }} data-testid="modal-footer">
				<Button
					onClick={form.handleClose}
					data-testid="modal-button-cancel"
					sx={{ textTransform: 'none', borderRadius: '8px' }}
				>
					{t('CANCEL')}
				</Button>
				<Button
					variant="contained"
					disableElevation
					onClick={() => void form.handleCreate()}
					disabled={
						!form.name.trim() ||
						!systemId ||
						form.isCreating ||
						!form.approvalsValid
					}
					data-testid="modal-button-create"
					sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
				>
					{t('CREATE')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
